import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import {
  CheckCheck,
  Maximize2,
  PhoneCall,
  Search,
  Send,
  X,
} from 'lucide-react';
import {
  apiCreateMessageThread,
  apiDeleteThreadMessage,
  apiGetThreadMessages,
  apiListTeamMembers,
  apiListMessageThreads,
  apiPostThreadMessage,
  apiPostThreadMessageAttachment,
  apiToggleThreadMessageReaction,
  fetchMessageAttachmentBlobUrl,
  formatThreadSidebarTime,
  mapChatMessageDto,
  mapMessageThreadSummaryDto,
  mapTeamMemberDtoToUi,
} from '../api/client';
import { TaskDescriptionEditor } from '../components/TaskDescriptionEditor';
import { FullscreenMediaOverlay } from '../components/FullscreenMediaOverlay';
import { MessageComposerAttachments } from '../components/MessageComposerAttachments';
import { hasReadableRichText, plainTextFromHtml } from '../lib/normalizeUploadFile';
import { sampleGroupForm, sampleMeetingLink } from '../lib/sampleData';
import { useWorkspaceAdds } from '../workspaceAddsContext';

function messagesFromSummaries(rows) {
  return rows.map((raw) => {
    const m = mapMessageThreadSummaryDto(raw);
    return {
      id: String(m.id),
      type: m.threadKind === 'group' ? 'group' : 'direct',
      name: m.name,
      avatar: m.avatar,
      online: m.online,
      lastMessage: m.previewText,
      timestamp: formatThreadSidebarTime(m.lastActivityUtc),
      unread: m.unreadCount,
      members: m.memberNames,
      lastActivityUtc: m.lastActivityUtc,
    };
  });
}

const CHAT_QUICK_REACTIONS = ['👍', '❤️', '😄', '🎉'];

function replyPreviewSnippet(messages, parentId, t) {
  const p = messages?.find((m) => String(m.id) === String(parentId));
  if (!p) return `${t('messageFallback')} #${parentId}`;
  return messagePreviewBubble(p, t);
}

function messagePreviewBubble(message, t) {
  if (message.type === 'image') return `${t('image')}: ${message.mediaLabel}`;
  if (message.type === 'video') return `${t('video')}: ${message.mediaLabel}`;
  if (message.type === 'audio') return `${t('audio')}: ${message.mediaLabel}`;
  if (message.type === 'file') return `${t('file')}: ${message.mediaLabel ?? message.attachmentOriginalFileName ?? ''}`;
  if (message.type === 'meeting') return `${t('meeting')}: ${message.link}`;
  if (message.type === 'html') {
    const text = plainTextFromHtml(message.text);
    if (!text) return t('messageFallback');
    return text.length > 140 ? `${text.slice(0, 140)}…` : text;
  }
  return message.text;
}

function MessageAttachmentPreview({ threadId, message, ownBubble, t }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState('');
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!message.hasAttachmentBlob || !threadId) return undefined;
    let dead = false;
    let revoked = null;
    (async () => {
      try {
        revoked = await fetchMessageAttachmentBlobUrl(threadId, message.id);
        if (!dead) setBlobUrl(revoked);
      } catch (e) {
        if (!dead) setErr(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      dead = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [threadId, message.hasAttachmentBlob, message.id]);

  if (!message.hasAttachmentBlob) return null;

  const k = message.attachmentKind || message.type || 'file';
  const border = ownBubble ? 'border-white/25' : 'border-brand-200 dark:border-brand-700';

  if (err) {
    return <p className="mt-1 text-[10px] opacity-95">{err}</p>;
  }
  if (!blobUrl) {
    return <span className="mt-1 block text-[10px] opacity-80">{t('loadingAttachment')}</span>;
  }

  if (k === 'image') {
    return (
      <>
        <button
          type="button"
          className={`mt-1 block w-full rounded-lg border p-0 text-left ${border}`}
          onClick={() => setLightbox({ src: blobUrl, mediaType: 'image' })}
          aria-label={t('viewImageFullScreen')}
        >
          <img
            src={blobUrl}
            alt=""
            className="max-h-52 max-w-full cursor-default rounded-lg object-contain"
          />
        </button>
        <FullscreenMediaOverlay
          open={!!lightbox?.src}
          onClose={() => setLightbox(null)}
          src={lightbox?.src ?? null}
          mediaType={lightbox?.mediaType ?? 'image'}
        />
      </>
    );
  }
  if (k === 'video') {
    return (
      <>
        <div className="relative mt-1 w-full">
          <video
            src={blobUrl}
            controls
            className={`max-h-52 w-full rounded-lg border object-contain ${border}`}
          />
          <button
            type="button"
            aria-label={t('fullScreenVideo')}
            className="absolute bottom-2 right-2 flex items-center justify-center rounded-lg bg-black/70 p-1.5 text-white shadow backdrop-blur-sm"
            onClick={() => setLightbox({ src: blobUrl, mediaType: 'video' })}
          >
            <Maximize2 size={16} strokeWidth={2} />
          </button>
        </div>
        <FullscreenMediaOverlay
          open={!!lightbox?.src}
          onClose={() => setLightbox(null)}
          src={lightbox?.src ?? null}
          mediaType={lightbox?.mediaType ?? 'video'}
        />
      </>
    );
  }
  if (k === 'audio') {
    return <audio src={blobUrl} controls className={`mt-1 block w-full min-w-[200px]`} />;
  }

  const name = message.attachmentOriginalFileName || message.mediaLabel || t('download');
  return (
    <a
      href={blobUrl}
      download={name}
      className={`mt-1 inline-block truncate text-[11px] font-medium underline ${ownBubble ? 'text-white' : 'text-brand-700 dark:text-brand-300'}`}
    >
      📎 {name}
    </a>
  );
}

/** @param {(msg: string) => void} [onToast] */
function MessagesPage({ t, EmptyState, onToast }) {
  const workspaceAddsAllowed = useWorkspaceAdds();
  const [tab, setTab] = useState('Direct');
  const [search, setSearch] = useState('');
  const [conversations, setConversations] = useState([]);
  const [messagesByThreadId, setMessagesByThreadId] = useState({});
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [composerHtml, setComposerHtml] = useState('');
  const [composeFile, setComposeFile] = useState(null);
  const [showNewDmModal, setShowNewDmModal] = useState(false);
  const [showNewGroupModal, setShowNewGroupModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [meetingLink, setMeetingLink] = useState('');
  const [groupForm, setGroupForm] = useState({
    name: '',
    avatar: 'GR',
    members: [],
  });
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [dmMemberId, setDmMemberId] = useState('');
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [replyToId, setReplyToId] = useState(null);

  const refreshThreads = useCallback(async () => {
    setLoadError('');
    const rows = await apiListMessageThreads();
    const next = messagesFromSummaries(rows);
    setConversations(next);
    return next;
  }, []);

  const conversationsRef = useRef(conversations);
  conversationsRef.current = conversations;

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoadingThreads(true);
      setLoadError('');
      try {
        const [threads, teamRows] = await Promise.all([apiListMessageThreads(), apiListTeamMembers()]);
        if (cancelled) return;
        const mapped = teamRows.map(mapTeamMemberDtoToUi);
        setWorkspaceMembers(mapped);
        setDmMemberId((prev) => (prev || (mapped.length ? mapped[0].id : '')));
        const next = messagesFromSummaries(threads);
        setConversations(next);
        setActiveConversationId((prev) => {
          if (prev && next.some((c) => c.id === prev)) return prev;
          return next.length ? next[0].id : null;
        });
      } catch (err) {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoadingThreads(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const activeConversation =
    conversations.find((item) => item.id === activeConversationId) || null;

  const peerNameForActive = activeConversation?.name ?? '';

  useEffect(() => {
    setReplyToId(null);
  }, [activeConversationId]);

  const toggleReaction = useCallback(
    async (messageId, emoji) => {
      if (!activeConversationId || !emoji) return;
      try {
        const updated = await apiToggleThreadMessageReaction(activeConversationId, messageId, emoji);
        const mapped = mapChatMessageDto(updated, peerNameForActive);
        setMessagesByThreadId((prev) => {
          const list = prev[activeConversationId] || [];
          return {
            ...prev,
            [activeConversationId]: list.map((m) => (String(m.id) === String(messageId) ? mapped : m)),
          };
        });
      } catch (err) {
        if (onToast) onToast(err instanceof Error ? err.message : String(err));
      }
    },
    [activeConversationId, peerNameForActive, onToast],
  );

  useEffect(() => {
    if (!activeConversationId) return;
    let cancelled = false;
    const run = async () => {
      setLoadingMessages(true);
      try {
        const rows = await apiGetThreadMessages(activeConversationId);
        if (cancelled) return;
        const name =
          conversationsRef.current.find((c) => c.id === activeConversationId)?.name ?? '';
        const mapped = rows.map((raw) => mapChatMessageDto(raw, name));
        setMessagesByThreadId((prev) => ({ ...prev, [activeConversationId]: mapped }));
        await refreshThreads();
      } catch (err) {
        if (!cancelled && onToast) onToast(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoadingMessages(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [activeConversationId, refreshThreads, onToast]);

  const visibleConversations = useMemo(
    () =>
      conversations.filter((item) => {
        const matchesTab = tab === 'Direct' ? item.type === 'direct' : item.type === 'group';
        const term = search.trim().toLowerCase();
        const matchesSearch = !term || item.name.toLowerCase().includes(term);
        return matchesTab && matchesSearch;
      }),
    [conversations, tab, search],
  );

  const activeMessages = activeConversationId ? messagesByThreadId[activeConversationId] : undefined;

  const updateConversationLocal = (conversationId, updater) => {
    setConversations((prev) => prev.map((item) => (item.id === conversationId ? updater(item) : item)));
  };

  const notify = (msg) => {
    if (onToast) onToast(msg);
  };

  return (
    <section className="grid gap-4 lg:grid-cols-12">
      <aside className="rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900 lg:col-span-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="inline-flex rounded-lg border border-brand-200 p-1 dark:border-brand-900">
            {[
              ['Direct', t('direct')],
              ['Groups', t('groups')],
            ].map(([typeTab, label]) => (
              <button
                key={typeTab}
                type="button"
                onClick={() => setTab(typeTab)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium ${
                  tab === typeTab
                    ? 'bg-brand-500 text-white'
                    : 'text-slate-600 dark:text-slate-300'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {workspaceAddsAllowed && (
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => setShowNewDmModal(true)}
                className="rounded-md border border-brand-200 px-2 py-1 text-[11px] dark:border-brand-900"
              >
                {t('newMessage')}
              </button>
              <button
                type="button"
                onClick={() => setShowNewGroupModal(true)}
                className="rounded-md border border-brand-200 px-2 py-1 text-[11px] dark:border-brand-900"
              >
                {t('newGroup')}
              </button>
            </div>
          )}
        </div>

        <div className="relative mb-3">
          <Search
            size={13}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('searchConversations')}
            className="w-full rounded-lg border border-brand-200 bg-white py-1.5 pl-8 pr-2.5 text-xs outline-none ring-brand-300 focus:ring dark:border-brand-900 dark:bg-slate-950"
          />
        </div>

        {loadError && (
          <p className="mb-2 text-xs text-red-600 dark:text-red-400" role="alert">
            {loadError}
          </p>
        )}

        <div className="space-y-1.5">
          {loadingThreads ? (
            <p className="px-2 text-xs text-slate-500 dark:text-slate-400">{t('loadingConversations')}</p>
          ) : visibleConversations.length === 0 ? (
            <p className="px-2 text-xs text-slate-500 dark:text-slate-400">{t('noConversationsYet')}</p>
          ) : (
            visibleConversations.map((conversation) => (
              <button
                key={conversation.id}
                type="button"
                onClick={() => setActiveConversationId(conversation.id)}
                className={`flex w-full items-center justify-between rounded-lg p-2 text-left ${
                  activeConversation?.id === conversation.id
                    ? 'bg-brand-100 dark:bg-brand-900/25'
                    : 'hover:bg-brand-50 dark:hover:bg-brand-900/20'
                }`}
              >
                <div className="min-w-0">
                  <p className="truncate text-xs font-medium text-slate-700 dark:text-slate-200">
                    {conversation.name}
                  </p>
                  <p className="truncate text-[11px] text-slate-500 dark:text-slate-300">
                    {conversation.lastMessage}
                  </p>
                </div>
                <div className="ml-2 text-right">
                  <p className="text-[10px] text-slate-400">{conversation.timestamp}</p>
                  {conversation.unread > 0 && (
                    <span className="inline-block rounded-full bg-brand-600 px-1.5 py-0.5 text-[10px] text-white">
                      {conversation.unread}
                    </span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </aside>

      <div className="flex min-h-[min(560px,calc(100vh-9rem))] flex-col rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 lg:col-span-8">
        {activeConversation ? (
          <>
            <header className="flex shrink-0 items-center justify-between border-b border-brand-200 px-3 py-2.5 dark:border-brand-900">
              <div>
                <p className="text-sm font-semibold text-slate-900 dark:text-white">
                  {activeConversation.name}
                </p>
                <p className="text-[11px] text-slate-500 dark:text-slate-300">
                  {activeConversation.type === 'group'
                    ? `${activeConversation.members?.length || 0} ${t('members')}`
                    : activeConversation.online
                      ? t('online')
                      : t('offline')}
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setShowMeetingModal(true)}
                  className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-2 py-1 text-[11px] dark:border-brand-900"
                >
                  <PhoneCall size={11} />
                  {t('shareMeetingLink')}
                </button>
                <button
                  type="button"
                  className="rounded-md border border-brand-200 p-1 dark:border-brand-900"
                >
                  <Search size={12} />
                </button>
              </div>
            </header>

            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
              {loadingMessages && (
                <p className="text-center text-xs text-slate-500 dark:text-slate-400">{t('loadingMessages')}</p>
              )}
              {!loadingMessages && (!activeMessages || activeMessages.length === 0) ? (
                <EmptyState
                  icon="💬"
                  title={t('noMessages')}
                  description={t('noMessagesDesc')}
                  ctaLabel={t('startConversation')}
                  onCta={() => setShowNewDmModal(true)}
                />
              ) : (
                activeMessages?.map((message) => (
                  <div
                    key={message.id}
                    className={`flex flex-col gap-1 ${message.own ? 'items-end' : 'items-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-xl px-3 py-2 text-xs ${
                        message.own
                          ? 'bg-brand-500 text-white'
                          : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200'
                      } ${message.parentMessageId ? 'ring-1 ring-inset ring-brand-400/25' : ''}`}
                    >
                      {message.parentMessageId ? (
                        <p
                          className={`mb-1 max-w-full truncate border-l-2 pl-2 text-[10px] opacity-90 ${
                            message.own ? 'border-white/50' : 'border-brand-400 dark:border-brand-500'
                          }`}
                          title={replyPreviewSnippet(activeMessages, message.parentMessageId, t)}
                        >
                          ↩ {replyPreviewSnippet(activeMessages, message.parentMessageId, t)}
                        </p>
                      ) : null}
                      {message.type === 'meeting' ? (
                        <div className="rounded-md border border-white/30 bg-white/10 p-2">
                          <p className="font-semibold">{t('meetingLinkShared')}</p>
                          <p className="mt-1 break-all text-[11px]">{message.link}</p>
                        </div>
                      ) : (
                        <>
                          {message.type === 'html' ? (
                            <div
                              className={`message-html-content leading-snug [&_a]:underline ${message.own ? 'text-white [&_a]:text-brand-100' : ''}`}
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(message.text, {
                                  ALLOWED_TAGS: [
                                    'a',
                                    'b',
                                    'blockquote',
                                    'br',
                                    'code',
                                    'em',
                                    'h2',
                                    'h3',
                                    'i',
                                    'li',
                                    'ol',
                                    'p',
                                    's',
                                    'strong',
                                    'u',
                                    'ul',
                                  ],
                                  ALLOWED_ATTR: ['href', 'target', 'rel'],
                                }),
                              }}
                            />
                          ) : (
                            <p>{message.text}</p>
                          )}
                          {message.hasAttachmentBlob && (
                            <MessageAttachmentPreview threadId={activeConversationId} message={message} ownBubble={!!message.own} t={t} />
                          )}
                          {!message.hasAttachmentBlob &&
                            (message.type === 'image' ||
                              message.type === 'video' ||
                              message.type === 'audio' ||
                              message.type === 'file') && (
                              <div
                                className={`mt-1 rounded-md px-2 py-1 text-[11px] ${
                                  message.own ? 'border border-white/30 bg-white/10' : 'border border-brand-200 bg-brand-50/80 dark:border-brand-800 dark:bg-brand-950/40'
                                }`}
                              >
                                {messagePreviewBubble(message, t)}
                              </div>
                            )}
                        </>
                      )}
                      <div className="mt-1 flex items-center justify-between gap-2 text-[10px] opacity-85">
                        <span>{message.timestamp}</span>
                        {message.own && (
                          <span className="inline-flex items-center gap-1">
                            <CheckCheck size={10} />
                            {message.read ? t('read') : t('sent')}
                          </span>
                        )}
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1 border-t border-current/10 pt-1.5">
                        {(message.reactions || []).map((r) => (
                          <button
                            key={r.emoji}
                            type="button"
                            onClick={() => void toggleReaction(message.id, r.emoji)}
                            className={`inline-flex items-center gap-0.5 rounded-full border px-2 py-0.5 text-[11px] leading-none ${
                              message.own
                                ? r.includesMe
                                  ? 'border-white/80 bg-white/25'
                                  : 'border-white/35 bg-white/10'
                                : r.includesMe
                                  ? 'border-brand-500 bg-brand-100 dark:border-brand-400 dark:bg-brand-900/50'
                                  : 'border-brand-200 bg-white dark:border-brand-800 dark:bg-slate-900/60'
                            }`}
                          >
                            <span>{r.emoji}</span>
                            <span className="font-semibold tabular-nums">{r.count}</span>
                          </button>
                        ))}
                        {CHAT_QUICK_REACTIONS.map((e) => (
                          <button
                            key={`add-${e}`}
                            type="button"
                            title={t('react')}
                            onClick={() => void toggleReaction(message.id, e)}
                            className={`rounded p-0.5 text-base leading-none opacity-80 hover:opacity-100 ${
                              message.own ? 'text-white' : 'text-slate-600 dark:text-slate-300'
                            }`}
                          >
                            {e}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => setReplyToId(String(message.id))}
                          className={`rounded border border-current/20 px-1.5 py-0.5 text-[10px] font-medium ${
                            message.own ? 'text-white' : 'text-brand-700 dark:text-brand-300'
                          }`}
                        >
                          {t('reply')}
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!activeConversationId) return;
                            try {
                              await apiDeleteThreadMessage(activeConversationId, message.id);
                              const rows = await apiGetThreadMessages(activeConversationId);
                              const mapped = rows.map((raw) => mapChatMessageDto(raw, peerNameForActive));
                              setMessagesByThreadId((prev) => ({
                                ...prev,
                                [activeConversationId]: mapped,
                              }));
                              if (replyToId === String(message.id)) setReplyToId(null);
                              await refreshThreads();
                            } catch (err) {
                              notify(err instanceof Error ? err.message : String(err));
                            }
                          }}
                          className={`rounded border border-current/20 px-1.5 py-0.5 text-[10px] font-medium ${
                            message.own ? 'text-white' : 'text-red-600 dark:text-red-400'
                          }`}
                        >
                          {t('delete')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="shrink-0 border-t border-brand-200 p-3 dark:border-brand-900">
              {replyToId && activeMessages && (
                <div className="mb-2 flex items-center justify-between gap-2 rounded-lg border border-brand-200 bg-brand-50/80 px-2.5 py-1.5 text-[11px] dark:border-brand-800 dark:bg-brand-950/40">
                  <span className="min-w-0 truncate text-slate-700 dark:text-slate-200">
                    <span className="font-medium text-brand-700 dark:text-brand-300">{t('replyTo')}</span>{' '}
                    {replyPreviewSnippet(activeMessages, replyToId, t)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setReplyToId(null)}
                    className="shrink-0 rounded-md border border-brand-200 px-2 py-0.5 text-[10px] dark:border-brand-800"
                  >
                    {t('cancel')}
                  </button>
                </div>
              )}
              <MessageComposerAttachments
                disabled={loadingMessages || !activeConversationId}
                t={t}
                onFileReady={(file) => setComposeFile(file)}
              />
              {composeFile && (
                <div className="mb-2 inline-flex items-center gap-2 rounded-md border border-brand-200 bg-brand-50 px-2 py-1 text-[11px] dark:border-brand-900 dark:bg-brand-900/10">
                  <span className="max-w-[200px] truncate">{composeFile.name}</span>
                  <button type="button" aria-label={t('close')} onClick={() => setComposeFile(null)}>
                    <X size={12} />
                  </button>
                </div>
              )}
              <div className="flex flex-col gap-2 lg:flex-row lg:items-end">
                <div className="min-w-0 flex-1">
                  <TaskDescriptionEditor
                    compact
                    value={composerHtml}
                    onChange={setComposerHtml}
                    disabled={loadingMessages || !activeConversationId}
                    placeholder={t('messageComposerPlaceholder')}
                    t={t}
                  />
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    if (!activeConversationId) return;
                    try {
                      if (composeFile) {
                        const caption = plainTextFromHtml(composerHtml);
                        const created = await apiPostThreadMessageAttachment(
                          activeConversationId,
                          composeFile,
                          caption,
                          replyToId ? Number(replyToId) : null,
                        );
                        const bubble = mapChatMessageDto(created, peerNameForActive);
                        setMessagesByThreadId((prev) => ({
                          ...prev,
                          [activeConversationId]: [...(prev[activeConversationId] || []), bubble],
                        }));
                        updateConversationLocal(activeConversationId, (item) => ({
                          ...item,
                          lastMessage: messagePreviewBubble(bubble, t),
                          timestamp: bubble.timestamp,
                        }));
                        setComposerHtml('');
                        setComposeFile(null);
                        setReplyToId(null);
                        await refreshThreads();
                        return;
                      }

                      if (!hasReadableRichText(composerHtml)) return;
                      const payload = {
                        contentType: 'html',
                        body: composerHtml.trim(),
                        ...(replyToId ? { parentMessageId: Number(replyToId) } : {}),
                      };
                      const created = await apiPostThreadMessage(activeConversationId, payload);
                      const bubble = mapChatMessageDto(created, peerNameForActive);
                      setMessagesByThreadId((prev) => ({
                        ...prev,
                        [activeConversationId]: [...(prev[activeConversationId] || []), bubble],
                      }));
                      updateConversationLocal(activeConversationId, (item) => ({
                        ...item,
                        lastMessage: messagePreviewBubble(bubble, t),
                        timestamp: bubble.timestamp,
                      }));
                      setComposerHtml('');
                      setReplyToId(null);
                      await refreshThreads();
                    } catch (err) {
                      notify(err instanceof Error ? err.message : String(err));
                    }
                  }}
                  className="inline-flex shrink-0 items-center justify-center gap-1 rounded-lg bg-brand-500 px-4 py-2.5 text-xs font-semibold text-white lg:py-2"
                >
                  <Send size={12} />
                  {t('send')}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="p-6 text-sm text-slate-500 dark:text-slate-300">
            {t('selectConversation')}
          </div>
        )}
      </div>

      {showMeetingModal && activeConversation && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-md rounded-xl border border-brand-200 bg-white p-4 shadow-xl dark:border-brand-900 dark:bg-slate-900">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
              {t('shareMeetingLink')}
            </h3>
            <input
              value={meetingLink}
              onChange={(event) => setMeetingLink(event.target.value)}
              placeholder={t('meetingLinkPlaceholder')}
              className="mt-3 w-full rounded-lg border border-brand-200 bg-white px-3 py-2 text-xs outline-none ring-brand-300 focus:ring dark:border-brand-900 dark:bg-slate-950"
            />
            <div className="mt-3 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => setMeetingLink(sampleMeetingLink())}
                className="mr-auto rounded-lg border border-dashed border-brand-300 px-3 py-1.5 text-xs font-medium text-brand-800 hover:bg-brand-50 dark:border-brand-700 dark:text-brand-200 dark:hover:bg-brand-950/50"
              >
                {t('fillSampleData')}
              </button>
              <button
                type="button"
                onClick={() => setShowMeetingModal(false)}
                className="rounded-lg border border-brand-200 px-3 py-1.5 text-xs dark:border-brand-900"
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                onClick={async () => {
                  const link = meetingLink.trim();
                  if (!link || !activeConversationId) return;
                  try {
                    const created = await apiPostThreadMessage(activeConversationId, {
                      contentType: 'meeting',
                      body: t('meetingLinkBody'),
                      linkUrl: link,
                    });
                    const bubble = mapChatMessageDto(created, peerNameForActive);
                    setMessagesByThreadId((prev) => ({
                      ...prev,
                      [activeConversationId]: [...(prev[activeConversationId] || []), bubble],
                    }));
                    await refreshThreads();
                    setMeetingLink('');
                    setShowMeetingModal(false);
                  } catch (err) {
                    notify(err instanceof Error ? err.message : String(err));
                  }
                }}
                className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white"
              >
                {t('share')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNewDmModal && workspaceAddsAllowed && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-sm rounded-xl border border-brand-200 bg-white p-4 shadow-xl dark:border-brand-900 dark:bg-slate-900">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{t('messagesTitle')}</h3>
            <select
              value={dmMemberId}
              onChange={(event) => setDmMemberId(event.target.value)}
              className="mt-3 w-full rounded-lg border border-brand-200 bg-white px-3 py-2 text-xs dark:border-brand-900 dark:bg-slate-950"
            >
              {workspaceMembers.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.name}
                </option>
              ))}
            </select>
            {!workspaceMembers.length && (
              <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
                {t('addPeopleForDm')}
              </p>
            )}
            <div className="mt-3 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                disabled={!workspaceMembers.length}
                onClick={() => {
                  const first = workspaceMembers[0];
                  if (!first) return;
                  setDmMemberId(String(first.id));
                }}
                className="mr-auto rounded-lg border border-dashed border-brand-300 px-3 py-1.5 text-xs font-medium text-brand-800 hover:bg-brand-50 disabled:opacity-40 dark:border-brand-700 dark:text-brand-200 dark:hover:bg-brand-950/50"
              >
                {t('fillSampleData')}
              </button>
              <button
                type="button"
                onClick={() => setShowNewDmModal(false)}
                className="rounded-lg border border-brand-200 px-3 py-1.5 text-xs dark:border-brand-900"
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                disabled={!workspaceMembers.length}
                onClick={async () => {
                  const m = workspaceMembers.find((x) => String(x.id) === String(dmMemberId));
                  if (!m) return;
                  try {
                    const created = await apiCreateMessageThread({
                      kind: 'direct',
                      participantName: m.name,
                      participantEmail: m.email,
                    });
                    const summary = messagesFromSummaries([created])[0];
                    setConversations((prev) => {
                      const rest = prev.filter((c) => c.id !== summary.id);
                      return [summary, ...rest];
                    });
                    setActiveConversationId(summary.id);
                    setTab('Direct');
                    setShowNewDmModal(false);
                    await refreshThreads();
                  } catch (err) {
                    notify(err instanceof Error ? err.message : String(err));
                  }
                }}
                className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
              >
                {t('start')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNewGroupModal && workspaceAddsAllowed && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-lg rounded-xl border border-brand-200 bg-white p-4 shadow-xl dark:border-brand-900 dark:bg-slate-900">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{t('newGroup').replace(/^\+\s*/, '')}</h3>
            <div className="mt-3 space-y-2 text-xs">
              <input
                value={groupForm.name}
                onChange={(event) =>
                  setGroupForm((prev) => ({ ...prev, name: event.target.value }))
                }
                placeholder={t('groupName')}
                className="w-full rounded-lg border border-brand-200 bg-white px-3 py-2 dark:border-brand-900 dark:bg-slate-950"
              />
              <input
                value={groupForm.avatar}
                onChange={(event) =>
                  setGroupForm((prev) => ({ ...prev, avatar: event.target.value.toUpperCase() }))
                }
                placeholder={t('groupAvatarInitials')}
                className="w-full rounded-lg border border-brand-200 bg-white px-3 py-2 dark:border-brand-900 dark:bg-slate-950"
              />
              <div className="max-h-32 space-y-1 overflow-y-auto rounded-lg border border-brand-200 p-2 dark:border-brand-900">
                {workspaceMembers.map((member) => (
                  <label key={member.id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={groupForm.members.includes(member.name)}
                      onChange={() =>
                        setGroupForm((prev) => ({
                          ...prev,
                          members: prev.members.includes(member.name)
                            ? prev.members.filter((item) => item !== member.name)
                            : [...prev.members, member.name],
                        }))
                      }
                    />
                    <span>{member.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="mt-3 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => setGroupForm(sampleGroupForm(workspaceMembers))}
                className="mr-auto rounded-lg border border-dashed border-brand-300 px-3 py-1.5 text-xs font-medium text-brand-800 hover:bg-brand-50 dark:border-brand-700 dark:text-brand-200 dark:hover:bg-brand-950/50"
              >
                {t('fillSampleData')}
              </button>
              <button
                type="button"
                onClick={() => setShowNewGroupModal(false)}
                className="rounded-lg border border-brand-200 px-3 py-1.5 text-xs dark:border-brand-900"
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!groupForm.name.trim()) return;
                  try {
                    const created = await apiCreateMessageThread({
                      kind: 'group',
                      title: groupForm.name.trim(),
                      memberNames: groupForm.members,
                    });
                    const summary = messagesFromSummaries([created])[0];
                    setConversations((prev) => {
                      const rest = prev.filter((c) => c.id !== summary.id);
                      return [summary, ...rest];
                    });
                    setActiveConversationId(summary.id);
                    setTab('Groups');
                    setShowNewGroupModal(false);
                    await refreshThreads();
                  } catch (err) {
                    notify(err instanceof Error ? err.message : String(err));
                  }
                }}
                className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white"
              >
                {t('createGroup')}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default MessagesPage;
